type CharOperation = 'e' | 's' | 'd' | 'i';
export type Token = string | [string, CharOperation?];

/*
 * Levenshtein distance implementation with backtrace.
 * Returns a list of tokens and together with selected operation.
 *
 * Arguments:
 *  - str1: string - string to compare
 *  - str2: string - reference string
 *
 * Returns an array of tokens. A token is a string, or a 2-element array.
 *
 * 'e' - equals
 * 's' - substitution
 * 'd' - deletion
 * 'i' - insertion
 *
 */
export function diffStrings(str1: string, str2: string): Array<Token> {
  let d: number[]; // current line distance
  let dp: number[] = []; // previous line distance

  const opBacktrace: Array<Array<CharOperation>> = [[]]; // backtrace

  for (let j = 0; j <= str2.length; j++) {
    dp[j] = j;
    opBacktrace[0][j] = 'd';
  }

  for (let i = 1; i <= str1.length; i++) {
    d = [i];
    opBacktrace[i] = ['i'];

    for (let j = 1; j <= str2.length; j++) {
      const eq = str1[i - 1] === str2[j - 1];
      const del = d[j - 1] + 1;
      const ins = dp[j] + 1;
      const sub = dp[j - 1] + (eq ? 0 : 1);

      d[j] = Math.min(del, ins, sub);

      if (sub === d[j]) {
        opBacktrace[i][j] = eq ? 'e' : 's';
      } else if (ins === d[j]) {
        opBacktrace[i][j] = 'i';
      } else {
        opBacktrace[i][j] = 'd';
      }
    }

    dp = d;
  }

  return generateTokens(str1, str2, opBacktrace);
}

const generateTokens = (
  str1: string,
  str2: string,
  opBacktrace: CharOperation[][],
): Array<Token> => {
  // Reconstruct tokens from backtrace
  let i1 = str1.length;
  let i2 = str2.length;
  const tokens: Array<Token> = [];

  while (i1 > 0 || i2 > 0) {
    const charOperation = opBacktrace[i1][i2];
    const prevToken = tokens[0];
    const char = charOperation === 'd' ? str2[i2 - 1] : str1[i1 - 1];

    if (charOperation === 'e') {
      if (typeof prevToken === 'string') {
        tokens[0] = addToToken(prevToken, char);
      } else {
        tokens.unshift(char);
      }
    } else {
      if (hasOperation(prevToken, charOperation)) {
        tokens[0] = addToToken(prevToken, char);
      } else {
        tokens.unshift([char, charOperation]);
      }
    }

    if (charOperation !== 'd') {
      i1--;
    }

    if (charOperation !== 'i') {
      i2--;
    }
  }

  return tokens;
};

const hasOperation = (token: Token, operation: CharOperation): boolean =>
  typeof token === 'object' ? token[1] === operation : false;

const addToToken = (token: Token, char: string): Token => {
  if (typeof token === 'string') {
    return char + token;
  }

  const [str, ...rest] = token;

  return [char + str, ...rest];
};
